import { message, PaginationProps } from "antd";
import type { ColumnsType } from "antd/es/table";
import { AxiosResponse } from "axios";
import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ConfirmPopup from "../../commons/components/ConfirmPopup/ConfirmPopup";
import CustomInput from "../../commons/components/CustomInput/CustomInput";
import CustomPagination from "../../commons/components/CustomPagination";
import CustomTable from "../../commons/components/CustomTable";
import PrimaryButton from "../../commons/components/PrimaryButton/PrimaryButton";

import Icon from "../../assets/images/Frame 1000003923.svg";
import ImageDisplay from "../../commons/components/ImageDisplay";
import { DELETE_SURPLUS, GET_PRODUCT_SURPLUS_REQUEST_URL } from "../../commons/constants/api-urls";
import { routerNavigation } from "../../commons/constants/routers";
import useFetchList from "../../commons/hooks/useFetchList";
import { EditIcon, EyeBoldIcon, TrashIcon } from "../../commons/resources";
import defaultAxios from "../../commons/utils/axios";
import { format2Digit } from "../../commons/utils/functions/formatPrice";
import { AppContext } from "../../contexts/AppContext";
import styles from "./surplus-management.module.scss";
import { SURPLUS_STATUS } from "../../commons/constants/surplus";
import { checkTimeWithCurrentTime } from "../../commons/utils/functions/formatDate";
import SoldOutFromPopup from "./SoldOutFormPopup/SoldOutFromPopup";
import { MODES } from "../../commons/constants/user";

interface DataType {
  key: React.Key;
  name: string;
  id: number;
  sellingPrice: number;
  costPrice: number;
  imageUrl: string[];
  startTime: string;
  endTime: string;
  status: string;
}

const params = { perPage: 10, paginationMetadataStyle: "body", type: "retail,food" };

const SurplusManagement: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const { user, currentStore, findAndSetStore } = useContext(AppContext);
  const { storeId } = useParams<{ storeId: string }>();
  const [perPage, setPerPage] = useState(10);
  const [keyword, setKeyword] = useState("");
  const [text, setText] = useState("");
  const [openConfirmPopupSoldOut, setOpenConfirmPopupSoldOut] = useState(false);

  const {
    data: surplusActive,
    total: totalActive,
    refresh: refreshActive,
  } = useFetchList<SurplusListItem>(GET_PRODUCT_SURPLUS_REQUEST_URL(storeId || ""), {
    ...params,
    page,
    keyword: keyword.trim(),
    perPage,
    sort_by: "create_time",
    order_by: "DESC",
  });

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const [openConfirmPopup, setOpenConfirmPopup] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Surplus>();

  const onShowSizeChange: PaginationProps["onShowSizeChange"] = (current, pageSize) => {
    setPerPage(pageSize || 10);
  };

  const deleteProduct = async (surplusId: string | number) => {
    try {
      const res: AxiosResponse<any> = await defaultAxios.delete(DELETE_SURPLUS(storeId || "", surplusId));
      if (!!res?.data) {
        message.success("Delete surplus successfully");
        refreshActive();
      }
    } catch (error) {
      console.log("delete surplus error", error);
      message.error("Delete surplus failed");
    }
  };

  const handleDeleteProduct = (product: Surplus) => {
    setSelectedProduct(product);
    setOpenConfirmPopup(true);
  };

  const handleMarkSurplusSoldOut = async (surplus: Surplus) => {
    setSelectedProduct(surplus);
    setOpenConfirmPopupSoldOut(true);
  };

  useEffect(() => {
    if (!storeId) return;
    findAndSetStore(storeId);
  }, [storeId]);

  const dataActive: DataType[] = useMemo(() => {
    if (!surplusActive || surplusActive.length === 0) return [];

    return surplusActive.map((product: any) => {
      return {
        key: product.id,
        name: product.name,
        imageUrl: product.imageUrl || Icon,
        id: product.id,
        sellingPrice: product.sellingPrice,
        costPrice: product.costPrice,
        startTime: product.startTime,
        endTime: product.endTime,
        status:
          product.status === SURPLUS_STATUS.SOLD_OUT
            ? "Sold out"
            : checkTimeWithCurrentTime(product.endTime as string)
            ? "Active"
            : "Expired",
        isSoldOut: product.status === SURPLUS_STATUS.SOLD_OUT,
      };
    });
  }, [surplusActive]);

  const columns: ColumnsType<DataType> = [
    {
      title: "Product name",
      dataIndex: "name",
      render: (_, record) => {
        return (
          <div
            style={{ width: "300px" }}
            className={`${styles.product} ${styles.pointer}`}
            onClick={() => navigate(routerNavigation.STORES.UPDATE_SURPLUS(currentStore?.id || "", record.id))}
          >
            <ImageDisplay src={record.imageUrl as unknown as string} alt={record.name} />
            <div className={styles.productName}>{record.name}</div>
          </div>
        );
      },
    },
    {
      title: "Product ID",
      dataIndex: "id",
      render: (text, record) => {
        return (
          <div
            style={{ width: "150px" }}
            onClick={() => navigate(routerNavigation.STORES.UPDATE_SURPLUS(currentStore?.id || "", record.id))}
            className={styles.pointer}
          >
            {text}
          </div>
        );
      },
    },
    {
      title: "Cost",
      dataIndex: "costPrice",
      render: (text, record) => {
        return (
          <div
            style={{ width: "70px" }}
            onClick={() => navigate(routerNavigation.STORES.UPDATE_SURPLUS(currentStore?.id || "", record.id))}
            className={styles.pointer}
          >
            <strong>$</strong> {format2Digit(text)}
          </div>
        );
      },
    },
    {
      title: "Selling price",
      dataIndex: "sellingPrice",
      render: (text, record) => {
        return (
          <div
            style={{ width: "70px" }}
            onClick={() => navigate(routerNavigation.STORES.UPDATE_SURPLUS(currentStore?.id || "", record.id))}
            className={styles.pointer}
          >
            <strong>$</strong> {format2Digit(text)}
          </div>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text, record) => {
        return (
          <div
            style={{ width: "70px" }}
            onClick={() => navigate(routerNavigation.STORES.UPDATE_SURPLUS(currentStore?.id || "", record.id))}
            className={text === "Active" ? styles.activeStatus : styles.expiredStatus}
          >
            {text}
          </div>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      render: (row: Surplus) => (
        <>
          <div className={styles.actions}>
            <div className={styles.edit}>
              {user?.merchant_role?.id === MODES.USER ? (
                <EyeBoldIcon
                  onClick={() => navigate(routerNavigation.STORES.UPDATE_SURPLUS(currentStore?.id || "", row.id))}
                />
              ) : (
                <EditIcon
                  onClick={() => navigate(routerNavigation.STORES.UPDATE_SURPLUS(currentStore?.id || "", row.id))}
                />
              )}
            </div>
            {user?.merchant_role?.id !== MODES.USER && (
              <div className={styles.delete} onClick={() => handleDeleteProduct(row)}>
                <TrashIcon />
              </div>
            )}
            {user?.merchant_role?.id !== MODES.USER && (
              <div
                className={`${styles.soldOut} ${row.isSoldOut && styles.true}`}
                onClick={() => handleMarkSurplusSoldOut(row)}
              >
                Sold out
              </div>
            )}
          </div>
        </>
      ),
    },
  ];

  useEffect(() => {
    const timeout = setTimeout(() => {
      setKeyword(text);
    }, 500);

    return () => clearTimeout(timeout);
  }, [text]);

  const handleChangeSearchInput = (event: any) => {
    setText(event.target.value.trim());
    refreshActive();
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(selectedRowKeys);
    },
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.titleWrapper}>
        <div className={styles.title}>Surplus</div>
        {user?.merchant_role?.id !== MODES.USER && (
          <PrimaryButton
            className={styles.addProductBtn}
            onClick={() => navigate(routerNavigation.STORES.CREATE_SURPLUS(currentStore?.id || ""))}
          >
            Add surplus product
          </PrimaryButton>
        )}
      </div>
      <CustomInput
        onChange={handleChangeSearchInput}
        className={styles.inputSearch}
        type="search"
        placeholder="Search for product name"
      />
      <div className={styles.productPanel}>
        <CustomTable
          className={styles.productTable}
          pagination={false}
          columns={columns}
          dataSource={dataActive}
          rowSelection={{
            type: "checkbox",
            ...rowSelection,
          }}
          scroll={{ x: 400 }}
        />

        <div className={styles.paginationWrapper}>
          <CustomPagination
            showLessItems={true}
            defaultCurrent={1}
            showSizeChanger
            current={page}
            onChange={page => setPage(page)}
            total={totalActive}
            onShowSizeChange={onShowSizeChange}
            pageSize={perPage}
          />
        </div>
      </div>

      <ConfirmPopup
        open={openConfirmPopup}
        onCancel={() => setOpenConfirmPopup(false)}
        title="Delete Product"
        description={`Do you want to delete ${selectedProduct?.name}?`}
        onConfirm={() => {
          deleteProduct(selectedProduct?.id || "");
          setOpenConfirmPopup(false);
        }}
      />

      {selectedProduct && (
        <SoldOutFromPopup
          open={openConfirmPopupSoldOut}
          onCancel={() => setOpenConfirmPopupSoldOut(false)}
          title="Sold Out"
          description="Please select items that are out of stock in this surplus"
          surplus={selectedProduct}
          onRefetch={refreshActive}
        />
      )}
    </div>
  );
};

export default SurplusManagement;
